/**
 * The wire format of the Realtime Database, mirroring the Android app's DTOs.
 *
 * This file and `FirebasePaths.kt` describe the same contract from opposite ends: a field renamed
 * here without being renamed there silently stops appearing on the athlete's phone.
 */

export type WeekdayKey =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export const WEEKDAYS: readonly WeekdayKey[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

export const WEEKDAY_LABEL: Record<WeekdayKey, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

/** How a piece of work is chained onto the set before it. */
export type SetKind = "superset" | "dropset" | "restPause";

export const SET_KINDS: readonly SetKind[] = ["superset", "dropset", "restPause"];

export const SET_KIND_LABEL: Record<SetKind, string> = {
  superset: "Superset",
  dropset: "Dropset",
  restPause: "Rest-pause",
};

/** What the weight is on, which decides how big the next step up is. */
export type Equipment = "barbell" | "dumbbell";

/**
 * A piece of work hung off a set.
 *
 * A dropset carries no exercise name and a rest-pause carries neither name nor load, because in
 * both cases they are the ones from the set it is attached to. Only a superset introduces a new
 * movement.
 */
export interface SetSegmentDto {
  kind: SetKind;
  exerciseName: string;
  equipment: Equipment;
  reps: number;
  weightKg: number;
  /** Rest-pause only: how long the pause is. */
  restSeconds: number;
}

export interface ExerciseDto {
  name: string;
  sets: number;
  reps: number;
  weightKg: number;
  restSeconds: number;
  /** Minutes, for work measured in time rather than repetitions. Zero for ordinary sets. */
  durationMinutes: number;
  equipment: Equipment;
  /** Blocks chained onto every set of this exercise. */
  extras: SetSegmentDto[];
  /** A ramp-up set: work that gets the movement warm rather than work the session is about. */
  warmup: boolean;
  /**
   * How the coach wants the movement done — tempo, cues, a range of motion to hold to.
   *
   * Written here, where there is a keyboard, and read on the phone behind an "i" beside the
   * exercise. Empty for the exercises nobody has written anything about, which is most of them.
   */
  notes: string;
}

export interface WorkoutDayDto {
  weekday: WeekdayKey;
  name: string;
  exercises: ExerciseDto[];
}

export interface ProgressiveOverloadDto {
  percentage: number;
  cycleWeeks: number;
  currentWeek: number;
  missedExercises: number;
}

export interface WorkoutPlanDto {
  id: string;
  name: string;
  createdAtMillis: number;
  nextSessionIndex: number;
  days: WorkoutDayDto[];
  progressiveOverload: ProgressiveOverloadDto;
}

/** Defaults the coach rarely needs to think about, but the app needs present. */
export const DEFAULT_REST_SECONDS = 90;
export const DEFAULT_CYCLE_WEEKS = 4;

export function emptyExercise(): ExerciseDto {
  return {
    name: "",
    sets: 3,
    reps: 10,
    weightKg: 0,
    restSeconds: DEFAULT_REST_SECONDS,
    durationMinutes: 0,
    equipment: "barbell",
    extras: [],
    warmup: false,
    notes: "",
  };
}

/**
 * A new block, starting from the set it hangs off.
 *
 * A superset is a different movement, so it starts blank; a dropset is the same movement lighter,
 * and a rest-pause is the same load again after a pause — so both carry the numbers above them
 * down, which is what the coach is about to change.
 */
export function newSegment(kind: SetKind, exercise: ExerciseDto): SetSegmentDto {
  return {
    kind,
    exerciseName: "",
    equipment: exercise.equipment,
    reps: Math.max(1, exercise.reps - 2),
    weightKg: kind === "dropset" ? Math.max(0, exercise.weightKg - 5) : exercise.weightKg,
    restSeconds: kind === "restPause" ? 15 : 0,
  };
}

export function emptyDay(weekday: WeekdayKey): WorkoutDayDto {
  return { weekday, name: "", exercises: [emptyExercise()] };
}

/** Drops the blank rows a half-filled table always carries. */
export function cleanPlan(plan: WorkoutPlanDto): WorkoutPlanDto {
  return {
    ...plan,
    days: plan.days
      .map((day) => ({
        ...day,
        exercises: day.exercises.filter((exercise) => exercise.name.trim().length > 0),
      }))
      .filter((day) => day.exercises.length > 0),
  };
}

export function planIsSendable(plan: WorkoutPlanDto): boolean {
  return plan.name.trim().length > 0 && cleanPlan(plan).days.length > 0;
}
